import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { EventLogType } from 'app/context/EventLogContext';
import EventLog from 'app/apiService/eventLog/eventLogApiService';
import { CircularProgress, Typography, Box } from '@mui/material';
import { shouldRetryQuery } from 'utils/queryRetry';
import EventLogCard from './EventLogCard';
import EventLogsFilterTray, { EventLogsFilters } from './EventLogsFilterTray';

const EventLogs = ({ userId }: { userId: string }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState<EventLogsFilters>({});
  const [eventTypesOrder, setEventTypesOrder] = useState<string[]>([]);

  useEffect(() => {
    EventLog.getEventLogTypes()
      .then(res => {
        if (res?.body && Array.isArray(res.body)) {
          setEventTypesOrder(res.body.map((o: { value: string }) => o.value));
        }
      })
      .catch(() => setEventTypesOrder([]));
  }, []);

  const fetchLogs = useCallback(
    async ({ pageParam = 1 }) => {
      const limit = 7;
      const skip = (pageParam - 1) * limit;
      const items: { columnField: string; operatorValue: string; value: string }[] = [];
      if (filters.search?.trim()) {
        items.push({ columnField: 'originating_ip', operatorValue: 'contains', value: filters.search.trim() });
      }
      if (filters.event_type) {
        items.push({ columnField: 'event_type', operatorValue: 'contains', value: filters.event_type });
      }
      if (filters.status) {
        items.push({ columnField: 'status', operatorValue: 'contains', value: filters.status });
      }
      const filtersParam = items.length > 0 ? JSON.stringify({ items }) : undefined;
      const response = await EventLog.getEventLogs({
        user_id: userId,
        skip,
        limit,
        filters: filtersParam,
      });
      return response.body;
    },
    [userId, filters],
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    error,
  } = useInfiniteQuery({
    queryKey: ['event-logs', userId, filters],
    queryFn: fetchLogs,
    getNextPageParam: last => {
      const { meta } = last;
      return meta.currentPage < meta.totalPages
        ? meta.currentPage + 1
        : undefined;
    },
    retry: shouldRetryQuery,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const handleFilterApply = useCallback((newFilters: EventLogsFilters) => {
    setFilters(newFilters);
  }, []);

  const allLogs: EventLogType[] =
    data?.pages.flatMap(page => page.data) || [];

  const lastPage = data?.pages?.length ? data.pages[data.pages.length - 1] : null;
  const currentPage = lastPage?.meta?.currentPage ?? 0;
  const totalPages = lastPage?.meta?.totalPages ?? 0;

  if (status === 'loading') {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', borderRadius: 1, overflow: 'hidden', backgroundColor: '#fff' }}>
        <EventLogsFilterTray
        onApply={handleFilterApply}
        initialFilters={filters}
        currentPage={currentPage}
        totalPages={totalPages}
      />
        <Box sx={{ width: '100%', height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', borderRadius: 1, overflow: 'hidden', backgroundColor: '#fff' }}>
        <EventLogsFilterTray
        onApply={handleFilterApply}
        initialFilters={filters}
        currentPage={currentPage}
        totalPages={totalPages}
      />
        <Box sx={{ width: '100%', height: 500, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="error">Error loading logs</Typography>
        </Box>
      </Box>
    );
  }

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop <=
        el.clientHeight + 100 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <EventLogsFilterTray
        onApply={handleFilterApply}
        initialFilters={filters}
        currentPage={currentPage}
        totalPages={totalPages}
      />
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        sx={{
          width: '100%',
          height: 500,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#f5f5f5',
        }}
      >
        {allLogs.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography>No logs available</Typography>
        </Box>
      ) : (
        allLogs.map(log => (
          <Box
            key={log._id}
            sx={{ flex: '0 0 auto', width: '100%', pl: 1, pr: 1, py: 0 }}
          >
            <EventLogCard log={log} eventTypesOrder={eventTypesOrder} />
          </Box>
        ))
      )}

        {isFetchingNextPage && hasNextPage && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: 2,
            }}
          >
            <CircularProgress size={24} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default EventLogs;
